import { useState } from 'react';
import { useInitializeUpload } from '@api';
import {
  parseError,
  UploadedFile,
  useFilesMutations,
  useFileUpload,
} from '@common';
import { FileRejection } from '@mantine/dropzone';
import { useTranslation } from 'react-i18next';

export type UseUploadFileModalMethodsReturnType = {
  opened: boolean;
  onClose: () => void;
  onDrop: (files: File) => void;
  onReject: (rejectedFiles: FileRejection) => void;
  uploadedFile: UploadedFile | null;
  isUploading: boolean;
  onSave: () => Promise<void>;
  error: string | null;
  openModal: (parentId: string) => void;
};

type UseUploadFileModalMethodsPropsType = {
  locationId: string;
  refetchFiles: () => void;
};
export const useUploadFileModalMethods = ({
  locationId,
  refetchFiles,
}: UseUploadFileModalMethodsPropsType): UseUploadFileModalMethodsReturnType => {
  const { t } = useTranslation();

  const [uploadFileParentId, setUploadFileParentId] = useState<string | null>(
    null
  );
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);

  const closeUploadFileModal = () => {
    setUploadFileParentId(null);
  };

  const { mutateAsync: initializeUpload } = useInitializeUpload();
  const { uploadFile, isUploading, uploadedFile, clearUploadedFile } =
    useFileUpload({
      initializeUpload,
    });
  const { saveUploadedFile } = useFilesMutations(refetchFiles);

  const handleSaveUploadedFile = async () => {
    if (!uploadFileParentId) return;
    if (!uploadedFile?.uuid) return;
    await saveUploadedFile({
      parentId: uploadFileParentId,
      fileUuid: uploadedFile.uuid,
      locationId,
    });
    clearUploadedFile();
  };

  const handleUploadFile = async (filesToUpload: File) => {
    setFileUploadError(null);
    try {
      await uploadFile(filesToUpload);
    } catch (e) {
      setFileUploadError(parseError(e) ?? t('UPLOAD_FILE_MODAL.GENERAL_ERROR'));
    }
  };

  const handleUploadFileReject = (rejectedFiles: FileRejection) => {
    if (!rejectedFiles.errors.length) return;

    setFileUploadError(rejectedFiles.errors[0].code);
  };

  return {
    opened: !!uploadFileParentId,
    onClose: closeUploadFileModal,
    onDrop: handleUploadFile,
    onReject: handleUploadFileReject,
    uploadedFile,
    isUploading,
    onSave: handleSaveUploadedFile,
    error: fileUploadError,
    openModal: setUploadFileParentId,
  };
};
